import { Button, Input } from '@cloudflare/kumo';
import { Select } from '@cloudflare/kumo/components/select';
import { ArrowDown, ArrowUp } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useId } from 'react';
import type { ContentObject, SiteSettings } from '../../shared/content.ts';
import { siteAssistantFormIssue } from '../../shared/site-assistant.ts';
import type { SiteAssistantQuickAction } from '../../shared/site-assistant.ts';

export function SiteAssistantConfigurationEditor({
  siteId,
  value,
  forms,
  onChange,
}: {
  siteId: string;
  value: SiteSettings;
  forms: Extract<ContentObject, { kind: 'Form' }>[];
  onChange: (value: SiteSettings) => void;
}) {
  const knowledgeId = useId();
  const selected = forms.find((form) => form.id === value.siteAssistantFormId);
  const issue = selected
    ? siteAssistantFormIssue(selected)
    : value.siteAssistantFormId
      ? 'Select a form from this site.'
      : undefined;
  return (
    <div className="grid gap-5">
      <p className="text-sm text-kumo-subtle">
        Choose a form for the assistant to collect conversationally. The assistant collects the form’s
        required fields. Supported types are text, email, phone, date, and single choice. Edit its fields and
        submission actions. Save changes, then publish the site to apply them.
      </p>
      <Select<string>
        label="Site Assistant form"
        aria-label="Site Assistant form"
        value={value.siteAssistantFormId ?? ''}
        items={{
          '': 'No form selected',
          ...Object.fromEntries(
            forms
              .filter((form) => !form.archived || form.id === value.siteAssistantFormId)
              .map((form) => [form.id, form.name]),
          ),
        }}
        onValueChange={(formId) => onChange({ ...value, siteAssistantFormId: formId || undefined })}
      />
      {issue && (
        <p role="alert" className="text-sm text-kumo-danger">
          {issue}
        </p>
      )}
      <div className="grid gap-2 text-sm">
        <label htmlFor={knowledgeId} className="font-semibold">
          Additional knowledge (optional)
        </label>
        <textarea
          id={knowledgeId}
          aria-describedby={`${knowledgeId}-help`}
          className="w-full min-h-36 border border-kumo-line rounded-lg p-3"
          maxLength={30_000}
          value={value.siteAssistantKnowledge ?? ''}
          onChange={(event) =>
            onChange({ ...value, siteAssistantKnowledge: event.target.value || undefined })
          }
        />
        <span id={`${knowledgeId}-help`} className="text-kumo-subtle">
          The assistant uses this additional knowledge to answer visitors’ questions alongside the{' '}
          <Link className="underline" to="/sites/$siteId/settings" params={{ siteId }}>
            business overview in Settings
          </Link>{' '}
          and the automatic summary of published pages.
        </span>
      </div>
      <QuickActions
        actions={value.siteAssistantQuickActions ?? []}
        onChange={(actions) => onChange({ ...value, siteAssistantQuickActions: actions })}
      />
    </div>
  );
}

function QuickActions({
  actions,
  onChange,
}: {
  actions: SiteAssistantQuickAction[];
  onChange: (actions: SiteAssistantQuickAction[]) => void;
}) {
  const update = (index: number, action: SiteAssistantQuickAction) =>
    onChange(actions.map((current, i) => (i === index ? action : current)));
  const move = (index: number, offset: number) => {
    const next = [...actions];
    const action = next.splice(index, 1)[0]!;
    next.splice(index + offset, 0, action);
    onChange(next);
  };
  return (
    <section className="grid gap-4" aria-label="Opening quick actions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[16px] font-semibold">Opening quick actions (optional)</h3>
        <Button
          disabled={actions.length >= 3}
          onClick={() => onChange([...actions, { kind: 'Prompt', message: '' }])}
        >
          Add quick action
        </Button>
      </div>
      <p className="text-sm text-kumo-subtle">
        Up to three buttons shown beneath the greeting before the visitor sends their first message. A prompt
        starts the chat; a link opens a web page or phone call.
      </p>
      {actions.map((action, index) => (
        <section
          key={index}
          aria-label={`Quick action ${index + 1}`}
          className="grid gap-4 border border-kumo-line rounded-lg p-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-semibold">Quick action {index + 1}</h4>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="ghost"
                shape="square"
                aria-label={`Move quick action ${index + 1} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUp aria-hidden="true" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                shape="square"
                aria-label={`Move quick action ${index + 1} down`}
                disabled={index === actions.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDown aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                aria-label={`Remove quick action ${index + 1}`}
                onClick={() => onChange(actions.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </div>
          </div>
          {action.kind === 'Link' && (
            <Input
              label="Quick action label"
              maxLength={80}
              value={action.label}
              onChange={(event) => update(index, { ...action, label: event.target.value })}
            />
          )}
          <Select<string>
            label="Quick action type"
            aria-label="Quick action type"
            value={action.kind}
            items={{ Prompt: 'Send a message', Link: 'Open a link' }}
            onValueChange={(kind) => {
              if (kind === action.kind) return;
              if (kind === 'Prompt') update(index, { kind, message: '' });
              else if (kind === 'Link') update(index, { kind, label: '', url: '' });
            }}
          />
          {action.kind === 'Prompt' ? (
            <Input
              label="Prompt message"
              maxLength={100}
              value={action.message}
              onChange={(event) => update(index, { ...action, message: event.target.value })}
            />
          ) : (
            <Input
              label="Link URL"
              value={action.url}
              onChange={(event) => update(index, { ...action, url: event.target.value })}
            />
          )}
        </section>
      ))}
    </section>
  );
}
